// Portal invoice client — links to /portal/invoices/:id (NOT the admin
// /invoices route — that's how franchisees ended up at the dashboard
// every time they clicked an invoice card).
import { useState } from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { ChevronRight, Calendar, Trash2, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import StatusBadge, { deriveDisplayStatus } from "@/components/invoices/StatusBadge";
import api from "@/lib/api";
import { toast } from "sonner";

const PortalInvoiceCard = ({ invoice, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);

  const formatDate = (dateStr) => {
    try { return format(new Date(dateStr), "MMM d, yyyy"); }
    catch { return dateStr; }
  };

  // Feb-2026 (Sam): swapped window.confirm for shadcn AlertDialog so
  // the confirm UX matches the admin card + the detail-page delete.
  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/portal/invoices/${invoice.id}`);
      toast.success("Invoice moved to deleted");
      onDeleted?.(invoice.id);
    } catch {
      toast.error("Could not delete invoice");
    } finally {
      setDeleting(false);
    }
  };

  // Only render the inline bin button on still-deletable statuses —
  // mirrors the admin-side rule (deleted invoices stay in /deleted).
  const canQuickDelete = invoice.status !== "deleted";

  const displayStatus = deriveDisplayStatus(invoice);
  const isOutstanding = displayStatus === "outstanding";
  const daysOverdue = invoice.days_overdue;
  // Franchisees don't reconcile — amount_owed is always ``total``, so
  // we only need the "X days overdue" hint (no separate owed sub-line).

  return (
    <Card className="p-4 hover:shadow-md transition-shadow rounded-lg group">
      <Link
        to={`/portal/invoices/${invoice.id}`}
        data-testid={`invoice-card-${invoice.id}`}
        className="block hover:bg-muted/30 -m-4 p-4 rounded-lg"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-mono text-sm font-semibold text-foreground">
              {invoice.invoice_number}
            </p>
            <p className="font-medium text-foreground mt-2 truncate">
              {invoice.client_name}
            </p>
            <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(invoice.issue_date)}</span>
              <span className="mx-1">·</span>
              <span>Due {formatDate(invoice.due_date)}</span>
            </div>
            {isOutstanding && typeof daysOverdue === "number" && daysOverdue > 0 && (
              <p
                className="text-xs font-semibold text-rose-600 mt-1"
                data-testid={`invoice-days-overdue-${invoice.id}`}
              >
                {daysOverdue} {daysOverdue === 1 ? "day" : "days"} overdue
              </p>
            )}
          </div>

          {/* Right-hand action area — status → amount → trash → arrow,
              all vertically centred (Feb-2026 Sam directive; mirrors
              the admin card layout so the pill no longer sits with
              the invoice number and nothing overlaps the price). */}
          <div className="flex items-center gap-3 shrink-0">
            <StatusBadge status={displayStatus} />

            <div className="text-right">
              <p className="font-mono text-lg font-semibold">
                £{Number(invoice.total).toFixed(2)}
              </p>
            </div>

            {canQuickDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); }}
                    disabled={deleting}
                    data-testid={`invoice-card-delete-${invoice.id}`}
                    title="Delete invoice"
                    className="w-8 h-8 rounded-md border border-stone-200 hover:border-rose-300 hover:bg-rose-50 flex items-center justify-center transition-colors"
                  >
                    {deleting
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin text-stone-500" />
                      : <Trash2 className="w-3.5 h-3.5 text-stone-500" />}
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent
                  data-testid={`invoice-card-delete-dialog-${invoice.id}`}
                >
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Invoice?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Move invoice <span className="font-mono font-semibold">{invoice.invoice_number}</span> to the deleted bin? You can restore it later.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`invoice-card-delete-cancel-${invoice.id}`}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      data-testid={`invoice-card-delete-confirm-${invoice.id}`}
                      className="bg-destructive text-destructive-foreground"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </div>
        </div>
      </Link>
    </Card>
  );
};

export default PortalInvoiceCard;
